import * as enabledApi from '@shared/api/enabledPlayApi';
import { StopAllOutputsCommand } from '@shared/constants/CommandValues';
import { EnabledPlayDesktopVirtualDeviceTypeId } from '@shared/constants/DeviceIds';
import { isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { DEVICES, VIRTUAL_DEVICE, VIRTUAL_DEVICE_ID, VIRTUAL_DEVICE_TOKEN } from '@shared/constants/StorageValues';
import { getNewDeviceName, isCommandInProfile } from '@shared/hooks/helpers/DeviceUtils';
import IUserAccess from '@shared/models/components/IUserAccess';
import MacroModel from '@shared/models/deviceProfiles/MacroModel';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import UpdateUserDeviceRequest from '@shared/models/devices/UpdateUserDeviceRequest';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import UserDeviceTokenModel from '@shared/models/devices/UserDeviceTokenModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';

/**
 * Manages all devices and api calls.
 * VirtualDevice = Cephable Desktop App, Virtual Controller. Works as both an input and output device.
 * OutputDevice = The device controlled by the panel. Ex: Panel microphone processes voice controls and dictation, then if it matches to an event / output, it sends the command to output device to be controlled.
 * @returns
 */
function useDeviceContainer() {
    const currentAppNameRef = useRef<string | null>(null);
    const userLicenseAccessRef = useRef<IUserAccess | undefined>(undefined);
    const [isSwitchingTempDisabled, setIsSwitchingTempDisabled, isSwitchingTempDisabledRef] = useStateRef(false);
    const [devices, setDevices, devicesRef] = useStateRef<UserDeviceModel[]>([]);
    const [virtualDeviceToken, setVirtualDeviceToken, virtualDeviceTokenRef] = useStateRef<UserDeviceTokenModel | null>(
        null
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errors, setErrors] = useState<string[]>([]);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [deviceCommandTokens, setDeviceCommandTokens] = useState<
        { userDeviceId: string; token: UserDeviceTokenModel }[]
    >([]);
    const [tryCommandHandlers, setTryCommandHandlers] = useState<
        ((command: string, profile: UserDeviceProfileModel) => boolean)[]
    >([]);
    const [outputDeviceId, setOutputDeviceId, outputDeviceIdRef] = useStateRef<string | null>(null);
    const [virtualDeviceId, setVirtualDeviceId, virtualDeviceIdRef] = useStateRef<string | null>(null);

    const addTryCommandHandler = (handler: (command: string, profile: UserDeviceProfileModel) => boolean) => {
        setTryCommandHandlers([...tryCommandHandlers, handler]);
    };
    const [tryGlobalControlHandlers, setTryGlobalControlHandlers] = useState<
        ((command: string, userDeviceId: string) => boolean)[]
    >([]);
    const addTryGlobalControlHandler = (handler: (command: string, userDeviceId: string) => boolean) => {
        setTryGlobalControlHandlers([...tryGlobalControlHandlers, handler]);
    };

    const initializeFromStorage = () => {
        try {
            setIsInitialized(false);
            const localDevices = localStorage.getItem(DEVICES);
            let storedDevices = [];

            if (localDevices) {
                storedDevices = JSON.parse(localDevices);
                setDevices(storedDevices);
            }

            const virtualDevice = getLocalVirtualDevice();
            setVirtualDeviceId(virtualDevice?.id ?? null);

            const virtualDeviceToken = getLocalVirtualDeviceToken();
            setVirtualDeviceToken(virtualDeviceToken);
        } catch (e) {
            console.log('Error loading devices from storage', e);
        } finally {
            setIsInitialized(true);
        }
    };

    const getLocalVirtualDevice = (): UserDeviceModel | null => {
        // Try ref first
        if (virtualDeviceIdRef.current) {
            const device = getLocalDevice(virtualDeviceIdRef.current);
            if (device) return device;
        }

        // get from storage
        const virtualDevice = localStorage.getItem(VIRTUAL_DEVICE);
        const virtualDeviceId = localStorage.getItem(VIRTUAL_DEVICE_ID);
        let device = virtualDevice ? JSON.parse(virtualDevice) : null;

        if (device) {
            return device;
        }

        return getLocalDevice(virtualDeviceId);
    };

    const getLocalVirtualDeviceToken = () => {
        // Try ref first
        if (virtualDeviceTokenRef.current) {
            return virtualDeviceTokenRef.current;
        }

        // get from storage
        const virtualDeviceTokenLocal = localStorage.getItem(VIRTUAL_DEVICE_TOKEN);
        if (virtualDeviceTokenLocal) {
            const deviceToken = JSON.parse(virtualDeviceTokenLocal);
            return deviceToken;
        }

        return null;
    };

    const initializeFromRemote = async (hardwareId: string | undefined, platform: string = 'generic') => {
        try {
            setIsInitialized(false);

            await initializeVirtualController(hardwareId, platform); // updates existing device, or registers a new one
            const deviceLoadResult = await getDevices(); // gets all devices including virtual controller

            return deviceLoadResult;
        } catch (error) {
            console.error('[ERROR initializeFromRemote]', error);
            return new UnexpectedResult();
        } finally {
            setIsInitialized(true);
            setIsLoading(false);
        }
    };

    const getDeviceName = (deviceId: string) => {
        const device = getLocalDevice(deviceId);
        if (device) {
            return device.nameOverride ?? device.device?.name ?? device.device.deviceType?.name ?? '-';
        }
        return '-';
    };

    const updateDevicePlatform = async (device: UserDeviceModel, platform: string = 'generic') => {
        if (device?.platform === platform) return;
        await updateDevice(device.id, {
            ...device,
            platform: platform,
        });
    };

    const registerNewVirtualDevice = async (
        hardwareId?: string,
        platform: string = 'generic'
    ): Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>> => {
        try {
            setIsLoading(true);

            const result = await enabledApi.registerNewDevice(
                EnabledPlayDesktopVirtualDeviceTypeId,
                getNewDeviceName(),
                hardwareId,
                platform
            );

            if (result?.resultType == 'Ok' && result.data) {
                setVirtualDeviceId(result.data.id);

                const tokenResult = await enabledApi.generateUserDeviceToken(result.data.id, {
                    includeUserEvents: true, // with this true, we will receive signalr events for the user side as well
                });
                if (tokenResult.resultType == 'Ok' && tokenResult.data) {
                    setVirtualDeviceToken(tokenResult.data);
                }
                snapshotState(devices, result.data.id, tokenResult.data ?? null);

                return new SuccessResult([result.data, tokenResult.data ?? null]);
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
            return { ...result, data: [null, null] };
        } catch (error) {
            setErrors([error]);
            console.debug('[ERROR registerNewVirtualDevice]', error);
            return new UnexpectedResult();
        } finally {
            setIsLoading(false);
        }
    };

    const initializeVirtualController = async (
        hardwareId?: string,
        platform: string = 'generic'
    ): Promise<IResult<[UserDeviceModel | null, UserDeviceTokenModel | null]>> => {
        try {
            const existingVirtualDevice = getLocalVirtualDevice();
            const existingToken = getLocalVirtualDeviceToken();

            if (existingVirtualDevice && existingToken) {
                //  update existing device's platform if not already set
                setVirtualDeviceId(existingVirtualDevice.id);
                setVirtualDeviceToken(existingToken);
                await updateDevicePlatform(existingVirtualDevice, platform);
                return new SuccessResult([existingVirtualDevice, existingToken]);
            }

            const result = await registerNewVirtualDevice(hardwareId, platform);
            return result;
        } catch (error) {
            setErrors([error]);
            console.debug('[ERROR initializeVirtualController]', error);
            return new UnexpectedResult();
        }
    };

    const getDevices = async (): Promise<IResult<UserDeviceModel[]>> => {
        try {
            setIsLoading(true);
            const result = await enabledApi.getUserDevices();
            if (result?.resultType == 'Ok' && result.data) {
                const loadedDevices = result.data.filter((d) => d.isVerified);
                setDevices(loadedDevices);
                snapshotState(loadedDevices, virtualDeviceIdRef.current, virtualDeviceToken);
                return new SuccessResult<UserDeviceModel[]>(loadedDevices);
            }
            return result;
        } catch (e) {
            setErrors(['Error getting devices']);
            return new UnexpectedResult<UserDeviceModel[]>();
        } finally {
            setIsLoading(false);
        }
    };

    const getLocalDevice = (userDeviceId: string | null): UserDeviceModel | null => {
        if (!userDeviceId) {
            return null;
        }

        const device = devicesRef.current?.find((d) => d.id == userDeviceId);
        return device ?? null;
    };

    const updateVirtualDevice = (device: UserDeviceModel) => {
        // legacy for web sub project
        updateLocalDevice(device);
    };

    const updateLocalDevice = (device: UserDeviceModel) => {
        const updatedDevices = _.cloneDeep(devicesRef.current);
        const index = devicesRef.current.findIndex((x) => x.id == device.id);

        if (index > -1) {
            updatedDevices[index] = device;
        } else {
            updatedDevices.push(device);
        }

        setDevices([...updatedDevices]);
        snapshotState(updatedDevices, virtualDeviceIdRef.current, virtualDeviceToken);
    };

    const snapshotState = (
        devices: UserDeviceModel[] | [],
        virtualDeviceId: string | null,
        virtualDeviceToken: UserDeviceTokenModel | null
    ) => {
        if (devices?.length > 0) localStorage.setItem(DEVICES, JSON.stringify(devices));
        if (virtualDeviceId) localStorage.setItem(VIRTUAL_DEVICE_ID, virtualDeviceId);
        if (virtualDeviceToken) localStorage.setItem(VIRTUAL_DEVICE_TOKEN, JSON.stringify(virtualDeviceToken));
    };

    const updateDevice = async (userDeviceId: string, update: UpdateUserDeviceRequest) => {
        const result = await enabledApi.updateUserDevice(userDeviceId, update);
        const updatedDevices = _.cloneDeep(devicesRef.current);
        if (result?.resultType == 'Ok' && result.data) {
            const deviceIndex = devicesRef.current?.findIndex((x) => x.id == userDeviceId);
            if (deviceIndex != null) {
                updatedDevices[deviceIndex] = result.data;

                setDevices([...updatedDevices]);
                snapshotState(updatedDevices, virtualDeviceIdRef.current, virtualDeviceToken);
            }
        }

        return result;
    };

    const checkForUpdates = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        const result = await enabledApi.updateDeviceFirmware(userDeviceId, token.token);

        return result;
    };

    const getDeviceToken = async (userDeviceId: string) => {
        if (userDeviceId == virtualDeviceIdRef.current) {
            return virtualDeviceTokenRef.current;
        }
        const existingToken = deviceCommandTokens.find((x) => x.userDeviceId == userDeviceId && !x.token.isDisabled);
        if (existingToken) {
            return existingToken.token;
        }
        const result = await enabledApi.generateCommandToken(userDeviceId);
        if (result?.resultType == 'Ok' && result.data) {
            setDeviceCommandTokens([...deviceCommandTokens, { userDeviceId, token: result.data }]);
            return result.data;
        }
    };

    const tryDeviceCommand = async (
        userDeviceId: string,
        command: string,
        profile: UserDeviceProfileModel,
        additionalInputContent?: string
    ) => {
        // if we are in the virtual controller then fire it locally
        // if we are not in the VC, then fire via API call
        if (userDeviceId == virtualDeviceIdRef.current) {
            if (profile) {
                for (let handler of tryCommandHandlers) {
                    if (handler(command, profile)) {
                        return true;
                    }
                }
            }
        } else {
            const device = devicesRef.current?.find((d) => d.id == userDeviceId);
            if (device && profile && !isCommandInProfile(command, profile)) {
                for (let handler of tryGlobalControlHandlers) {
                    if (handler(command, userDeviceId)) {
                        return true;
                    }
                }
                return false;
            }
            const macro = devicesRef.current
                ?.find((d) => d.id == userDeviceId)
                ?.currentProfile?.configuration?.macros?.find((m) =>
                    m.commands.some((c) => c.toLowerCase() == command.toLowerCase())
                );

            const sendResult = await sendDeviceCommand(userDeviceId, command, macro);
            return sendResult;
        }

        return false;
    };

    const tryDeviceTypeCommand = async (userDeviceId: string, phrase: string) => {
        const device = devicesRef.current?.find((d) => d.id == userDeviceId);
        if (device) {
            const token = await getDeviceToken(userDeviceId);
            if (token) {
                await enabledApi.sendDeviceTypeCommand(userDeviceId, phrase, token.token);
                return true;
            }
        }
        return false;
    };

    const sendDeviceCommand = async (userDeviceId: string, command: string, macro?: MacroModel) => {
        const device = devicesRef.current?.find((d) => d.id == userDeviceId);
        if (device) {
            const token = await getDeviceToken(userDeviceId);
            if (token) {
                await enabledApi.sendDeviceCommand(userDeviceId, command, token.token, macro);
                return true;
            }
        }
        return false;
    };

    const deleteDevice = async (userDeviceId: string) => {
        try {
            const result = await enabledApi.unregisterUserDevice(userDeviceId);
            if (result?.resultType == 'Ok') {
                const clone = _.cloneDeep(devicesRef.current);
                const updatedDevices = clone.filter((d) => d.id != userDeviceId);
                const isVirtualDevice = virtualDeviceIdRef.current == userDeviceId;
                if (isVirtualDevice) {
                    setVirtualDeviceId(null);
                    setVirtualDeviceToken(null);
                    localStorage.removeItem(VIRTUAL_DEVICE_ID);
                    localStorage.removeItem(VIRTUAL_DEVICE_TOKEN);

                    // backwards compatibility for anyone who has not signed out
                    localStorage.removeItem(VIRTUAL_DEVICE);
                }

                if (outputDeviceIdRef.current === userDeviceId) {
                    setOutputDeviceId(null);
                }
                setDevices(updatedDevices);
                snapshotState(updatedDevices, isVirtualDevice ? null : virtualDeviceIdRef.current, virtualDeviceToken);
            }
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const disableDevice = async (userDeviceId: string) => {
        try {
            const result = await enabledApi.disableDevice(userDeviceId);
            if (result?.resultType == 'Ok') {
                const updatedDevices = _.cloneDeep(devicesRef.current);
                const deviceIndex = devicesRef.current?.findIndex((x) => x.id == userDeviceId);
                if (deviceIndex !== -1) {
                    updatedDevices.splice(deviceIndex, 1);
                    const isVirtualDevice = virtualDeviceIdRef.current == userDeviceId;
                    if (isVirtualDevice) {
                        setVirtualDeviceId(null);
                        setVirtualDeviceToken(null);
                        localStorage.removeItem(VIRTUAL_DEVICE_ID);
                        localStorage.removeItem(VIRTUAL_DEVICE_TOKEN);

                        // backwards compatibility for anyone who has not signed out
                        localStorage.removeItem(VIRTUAL_DEVICE);
                    }

                    if (outputDeviceIdRef.current === userDeviceId) {
                        setOutputDeviceId(null);
                    }

                    setDevices(updatedDevices);
                    snapshotState(
                        updatedDevices,
                        isVirtualDevice ? null : virtualDeviceIdRef.current,
                        virtualDeviceToken
                    );
                }
            }
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const removeLocalDevice = (userDeviceId: string) => {
        const clone = _.cloneDeep(devicesRef.current);
        const updatedDevices = clone.filter((d) => d.id != userDeviceId);
        const isVirtualDevice = virtualDeviceIdRef.current == userDeviceId;
        setDevices(updatedDevices);
        snapshotState(updatedDevices, isVirtualDevice ? null : virtualDeviceIdRef.current, virtualDeviceToken);
    };

    const startListening = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        const result = await enabledApi.startDeviceListening(userDeviceId, token.token);
        return result;
    };

    const stopListening = async (userDeviceId: string) => {
        const token = await getDeviceToken(userDeviceId);
        if (!token) return new UnexpectedResult<UserDeviceModel>();
        const result = await enabledApi.stopDeviceListening(userDeviceId, token.token);
        return result;
    };
    const stopAllOutputs = async (userDeviceId: string, profile: UserDeviceProfileModel) => {
        tryDeviceCommand(userDeviceId, StopAllOutputsCommand, profile);
    };

    const isDeviceVirtualController = (deviceId: string): boolean => {
        // current output device is same as input device
        return deviceId === virtualDeviceIdRef.current;
    };

    const isAutoProfileSwitchingDisabled = (): boolean => {
        if (!outputDeviceIdRef.current) return true;

        const device = devicesRef.current?.find((d) => d.id == outputDeviceIdRef.current);

        return (
            !isDeviceVirtualController(outputDeviceIdRef.current) ||
            !device?.isAutoProfileSwitchingEnabled ||
            isSwitchingTempDisabledRef.current
        );
    };

    const clearDevices = () => {
        setDevices([]);
        setVirtualDeviceId(null);
        setVirtualDeviceToken(null);
        setOutputDeviceId(null);
        snapshotState([], null, null);
    };

    const isDesktopApp = (deviceTypeId: string) => {
        return deviceTypeId === EnabledPlayDesktopVirtualDeviceTypeId;
    };

    const setDeviceProfile = async (
        deviceId: string | undefined,
        profile: UserDeviceProfileModel
    ): Promise<IResult<UserDeviceModel>> => {
        try {
            if (!deviceId) {
                return new InvalidResult('Device ID is required to set profile');
            }

            const device = devicesRef.current?.find((d) => d.id == deviceId);
            if (!device) {
                return new InvalidResult('Device not found');
            }

            if (!isLicenseValidForProfile(profile, userLicenseAccessRef.current)) {
                return new InvalidResult('Invalid permissions. Cannot set device profile');
            }

            let result: IResult<UserDeviceModel> = new UnexpectedResult<UserDeviceModel>();
            let updatedDevice = device;

            // server call
            const profileUpdate = profile.isPublic
                ? { customProfileId: null, defaultProfileId: profile.id }
                : { customProfileId: profile.id };

            result = await enabledApi.updateDeviceProfile(deviceId, profileUpdate);

            if (result.resultType === 'Ok' && result.data) {
                updatedDevice = result.data;
            }

            if (result?.resultType === 'NoResponse' || result?.resultType === 'Offline') {
                // offline mode
                updatedDevice = _.cloneDeep(device);

                if (profile.isPublic) {
                    updatedDevice.currentProfile = null;
                    updatedDevice.defaultProfileId = profile.id;
                } else {
                    updatedDevice.currentProfile = profile;
                }

                result = new SuccessResult<UserDeviceModel>(updatedDevice);
            }

            updateLocalDevice(updatedDevice);
            return result;
        } catch (e) {
            return new UnexpectedResult<UserDeviceModel>();
        }
    };

    const resetDeviceProfile = async (deviceId: string | null, profile: UserDeviceProfileModel) => {
        if (!deviceId) {
            return new InvalidResult<UserDeviceModel>('Device ID is not set');
        }

        const device = devicesRef.current?.find((d) => d.id == deviceId);
        if (!device) {
            return new InvalidResult<UserDeviceModel>('Device not found');
        }

        let result: IResult<UserDeviceModel>;
        // reset current profile to null
        if (profile?.isPublic) {
            result = await enabledApi.updateDeviceProfile(deviceId, {
                defaultProfileId: null,
            });
        } else {
            result = await enabledApi.updateDeviceProfile(deviceId, {
                customProfileId: null,
            });
        }

        let updatedDevice = device;
        if (result.resultType === 'Ok' && result.data) {
            updatedDevice = result.data;
        } else if (result.resultType !== 'Ok') {
            // offline reset
            updatedDevice = _.cloneDeep(device);
            if (profile?.isPublic) {
                updatedDevice.defaultProfileId = null;
            } else {
                updatedDevice.currentProfile = null;
            }
            result = new SuccessResult<UserDeviceModel>(updatedDevice);
        }

        updateLocalDevice(updatedDevice);

        return result;
    };

    const updateCurrentAppName = (appName: string) => {
        if (appName !== currentAppNameRef.current) {
            currentAppNameRef.current = appName;
            fallbackToDeviceSwitching();
            return true;
        }
        return false;
    };

    const fallbackToDeviceSwitching = () => {
        setIsSwitchingTempDisabled(false);
    };

    const overrideAppSwitching = () => {
        if (!isSwitchingTempDisabledRef.current) {
            setIsSwitchingTempDisabled(true);
        }
    };

    /**
     * Sets the output device Id. This is the same as the device being controlled by the panel.
     * @param deviceId
     */
    const updateOutputDeviceId = (deviceId: string | null) => {
        if (deviceId !== outputDeviceIdRef.current) {
            setOutputDeviceId(deviceId);
            const device = getLocalDevice(deviceId);
            if (connectedDevices.length > 1 && device) {
                // only track if more than 1 device is connected to avoid
                publish('analyticsTrackEvent', {
                    eventName: 'output_device_switched',
                    data: {
                        deviceTypeId: device?.device?.deviceType?.id,
                    },
                });
            }
        }

        if (deviceId === virtualDeviceIdRef.current) {
            fallbackToDeviceSwitching();
        }
    };

    useEffect(() => {
        const unsubscribeUserLicenseChange = subscribe('userLicenseChangeLocal', async (data) => {
            if (data) {
                userLicenseAccessRef.current = data;
            }
        });

        return () => {
            unsubscribeUserLicenseChange();
        };
    }, []);

    const connectedDevices = useMemo(() => {
        const updatedConnectedDevices = devices.filter((d) => d.id === virtualDeviceId || d.isConnected);
        return updatedConnectedDevices;
    }, [devices]);

    return {
        connectedDevices,
        devices,
        devicesRef,
        setDevices,
        fallbackToDeviceSwitching,
        getLocalDevice,
        isAutoProfileSwitchingDisabled,
        isInitialized,
        isSwitchingTempDisabled,
        outputDeviceId,
        outputDeviceIdRef,
        overrideAppSwitching,
        setDeviceProfile,
        updateCurrentAppName,
        updateOutputDeviceId,
        virtualDeviceId,
        virtualDeviceIdRef,
        virtualDeviceToken,
        virtualDeviceTokenRef,
        isLoading,
        errors,
        clearDevices,
        updateLocalDevice,
        updateVirtualDevice,
        initializeFromStorage,
        initializeVirtualController,
        updateDevice,
        checkForUpdates,
        initializeFromRemote,
        isDesktopApp,
        getDevices,
        getDeviceName,
        deleteDevice,
        disableDevice,
        resetDeviceProfile,
        startListening,
        stopListening,
        stopAllOutputs,
        addTryCommandHandler,
        addTryGlobalControlHandler,
        tryDeviceTypeCommand,
        tryDeviceCommand,
        sendDeviceCommand,
        removeLocalDevice,
    };
}
const DeviceContainer = createContainer(useDeviceContainer);
export default DeviceContainer;
